import { useState, useMemo,useEffect } from 'react';
import { Parcel, SoilZones, Objective } from '../types';
import { generateScenario } from '../utils/voronoiGenerator';
import { computeMetrics, createSignature } from '../utils/metricsCalculator';
import { runOptimizer } from '../utils/optimizer';
import { loadGeoJSONParcels } from '../utils/geojsonLoader';

export function useLandUseState() {
  const [seed, setSeed] = useState(7);
    const [isLoading, setIsLoading] = useState(true);
  const [workingParcels, setWorkingParcels] = useState<Parcel[]>(() => generateScenario(7));
  const [workingSoil, setWorkingSoil] = useState<SoilZones>({
    organic: 'organic',
    mineral: 'mineral',
  });
  const [committedParcels, setCommittedParcels] = useState<Parcel[]>(() => generateScenario(7));
  const [committedSoil, setCommittedSoil] = useState<SoilZones>({
    organic: 'organic',
    mineral: 'mineral',
  });
  const [selectedParcelId, setSelectedParcelId] = useState<string | null>(null);
  const [changedByOptimizer, setChangedByOptimizer] = useState<Set<string>>(new Set());

  const isDirty = useMemo(
    () =>
      createSignature(workingParcels, workingSoil) !==
      createSignature(committedParcels, committedSoil),
    [workingParcels, workingSoil, committedParcels, committedSoil]
  );

  const metrics = useMemo(
    () => computeMetrics(committedParcels, committedSoil),
    [committedParcels, committedSoil]
  );

  const handleParcelReassign = (parcelId: string, newClass: string) => {
    setWorkingParcels((prev) =>
      prev.map((p) => (p.id === parcelId ? { ...p, cls: newClass } : p))
    );
    setChangedByOptimizer(new Set());
  };

  const handleSoilToggle = (zone: 'organic' | 'mineral') => {
    setWorkingSoil((prev) => ({
      ...prev,
      [zone]: prev[zone] === 'organic' ? 'mineral' : 'organic',
    }));
    setChangedByOptimizer(new Set());
  };

  const handleRecompute = () => {
    setCommittedParcels(workingParcels);
    setCommittedSoil(workingSoil);
  };

    useEffect(() => {
    // Load GeoJSON from public folder
    fetch('/data/land_use_optimizer/nass_polygons.geojson')
      .then((res) => res.json())
      .then((geojson) => {
        const parcels = loadGeoJSONParcels(geojson);
        setWorkingParcels(parcels);
        setCommittedParcels(parcels);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load GeoJSON:', err);
        // Fallback to synthetic data if needed
        const fallbackParcels = generateScenario(7);
        setWorkingParcels(fallbackParcels);
        setCommittedParcels(fallbackParcels);
        setIsLoading(false);
      });
  }, []);


  const handleReset = () => {
  setIsLoading(true);
  // Reload GeoJSON from scratch
  fetch('/data/land_use_optimizer/nass_polygons.geojson')
    .then((res) => res.json())
    .then((geojson) => {
      const parcels = loadGeoJSONParcels(geojson);
      setWorkingParcels(parcels);
      setCommittedParcels(parcels);
      // Reset soil zones
      const resetSoil = { organic: 'organic' as const, mineral: 'mineral' as const };
      setWorkingSoil(resetSoil);
      setCommittedSoil(resetSoil);
      setSelectedParcelId(null);
      setChangedByOptimizer(new Set());
      setIsLoading(false);
    })
    .catch((err) => {
      console.error('Failed to reload GeoJSON:', err);
      setIsLoading(false);
    });
};

    const handleNewScenario = () => {
    // Keep existing data or reload from file
    alert('GeoJSON data loaded, random generation disabled');
  };

  // const handleNewScenario = () => {
  //   const newSeed = Math.floor(Math.random() * 100000);
  //   setSeed(newSeed);
  //   const newParcels = generateScenario(newSeed);
  //   setWorkingParcels(newParcels);
  //   setCommittedParcels(newParcels);
  //   const resetSoil = { organic: 'organic' as const, mineral: 'mineral' as const };
  //   setWorkingSoil(resetSoil);
  //   setCommittedSoil(resetSoil);
  //   setSelectedParcelId(null);
  //   setChangedByOptimizer(new Set());
  // };

const handleRunOptimizer = (objective: Objective, maxPct: number, excludedClasses: Set<string>) => {
  const result = runOptimizer(committedParcels, committedSoil, objective, maxPct, excludedClasses);
  setWorkingParcels(result.optimizedParcels);
  setWorkingSoil({ ...committedSoil });
  setSelectedParcelId(null);
  setChangedByOptimizer(result.changedIds);
  return result;
};

  return {
    seed,
    isLoading,
    workingParcels,
    workingSoil,
    committedParcels,
    committedSoil,
    selectedParcelId,
    changedByOptimizer,
    isDirty,
    metrics,
    setSelectedParcelId,
    handleParcelReassign,
    handleSoilToggle,
    handleRecompute,
    handleNewScenario,
    handleReset,
    handleRunOptimizer,
  };
}